import {
  MAX_FILE_SIZE,
  SUPPORTED_DOCUMENT_FILE_TYPES
} from "@/modules/application/common/constants";
import {
  CardReissuanceReason,
  InstructionActions,
  ReissuedCardReceivingType
} from "@/modules/application/common/enum";
import { LATIN_NUMERIC_REGEX, ONLY_DIGITS } from "@/shared/common/constants";
import { ErrorMessages } from "@/shared/common/enums";
import type {
  ValidationSchemaPeriodicalPaymentsProps,
  ValidationSchemaProps
} from "@/shared/common/types";
import {
  getAccountNumberValidationSchema,
  getAmountValidationSchema,
  getApplicationNumberValidationSchema,
  getBeneficiaryNameValidationSchema,
  getCardNumberValidationSchema,
  getPhoneNumberValidationSchema,
  getPurposeValidationSchema,
  getRequiredStringValidationSchema,
  getStringInputValidationSchema,
} from "@/shared/validations";
import * as Yup from "yup";

export const accountOpeningApplicationValidationSchema = (
  { t }: ValidationSchemaProps) =>
  Yup.object().shape({
    applicationNumber: getApplicationNumberValidationSchema({ t }),
    purpose: getPurposeValidationSchema({ t }),
    currency: getRequiredStringValidationSchema({ t }),
    isAgreeWithConditions: Yup.boolean()
      .required(t(ErrorMessages.MustBeAccepted))
      .oneOf([true], t(ErrorMessages.MustBeAccepted)),
  });

export const accountClosureApplicationValidationSchema = (
  { t }: ValidationSchemaProps) =>
  Yup.object().shape({
    applicationNumber: getApplicationNumberValidationSchema({ t }),
    purpose: getPurposeValidationSchema({ t }),
    closureAccount: getRequiredStringValidationSchema({ t }),
    isAgreeWithConditions: Yup.boolean()
      .required(t(ErrorMessages.MustBeAccepted))
      .oneOf([true], t(ErrorMessages.MustBeAccepted)),
  });

export const phoneBankingApplicationValidationSchema = (
  { t }: ValidationSchemaProps) =>
  Yup.object().shape({
    applicationNumber: getApplicationNumberValidationSchema({ t }),
    purpose: getPurposeValidationSchema({ t }),
    isAgreeWithConditions: Yup.boolean()
      .required(t(ErrorMessages.MustBeAccepted))
      .oneOf([true], t(ErrorMessages.MustBeAccepted)),
    isReceiveInformation: Yup.boolean()
      .test(
        "at-least-one-true",
        t("errorMessages.atLeastOneRequired"),
        function (value) {
          const { isDefineExchangeRate } = this.parent;
          return !!value || !!isDefineExchangeRate;
        }
      ),
    isDefineExchangeRate: Yup.boolean()
      .test(
        "at-least-one-true",
        t("errorMessages.atLeastOneRequired"),
        function (value) {
          const { isReceiveInformation } = this.parent;
          return !!value || !!isReceiveInformation;
        }
      ),
  }, [["isReceiveInformation", "isDefineExchangeRate"]]);

export const payrollCardOpeningApplicationValidationSchema = (
  { t }: ValidationSchemaProps) =>
  Yup.object().shape({
    fullName: getStringInputValidationSchema({ t }),
    address: getRequiredStringValidationSchema({ t })
      .min(5, t(ErrorMessages.MinInput, { count: 5 })),
    purpose: getPurposeValidationSchema({ t }),
    ssn: Yup.string()
      .required(t(ErrorMessages.RequiredField))
      .matches(ONLY_DIGITS, t(ErrorMessages.OnlyDigits))
      .length(10, t(ErrorMessages.SsnInvalidLength, { length: 10 }),),
    email: getRequiredStringValidationSchema({ t })
      .email(t(ErrorMessages.InvalidEmail)),
    phoneNumber: getPhoneNumberValidationSchema({ t }),
    applicationNumber: getApplicationNumberValidationSchema({ t }),
  });

export const periodicalPaymentsApplicationValidationSchema = (
  { t, isAccountNumberInvalid }: ValidationSchemaPeriodicalPaymentsProps) =>
  Yup.object().shape({
    debitAccount: getRequiredStringValidationSchema({ t }),
    amount: getAmountValidationSchema({ t }),
    sendEmailNotification: Yup.boolean().required(t(ErrorMessages.RequiredField)),
    email: Yup.string().nullable()
      .when("sendEmailNotification", {
        is: true,
        then: (schema) => schema.email(t(ErrorMessages.InvalidEmail)),
        otherwise: (schema) => schema,
      }),
    creditAccount: getAccountNumberValidationSchema({ t, isAccountNumberInvalid }),
    beneficiary: getBeneficiaryNameValidationSchema({ t }),
    beneficiaryBank: getRequiredStringValidationSchema({ t }),
    workingDayAdjustment: getRequiredStringValidationSchema({ t }),
    frequencyInfo: getStringInputValidationSchema({ t }),
    startDate: getRequiredStringValidationSchema({ t }),
    endDate: Yup.string().nullable()
      .when("startDate", (startDate, schema) => {
        return startDate
          ? schema.test(
            "is-after-start-date",
            t(ErrorMessages.EndDate),
            (endDate) => {
              if (!endDate) return true;
              return new Date(endDate) > new Date(`${startDate}`);
            }
          )
          : schema;
      }),
    purpose: getPurposeValidationSchema({ t }),
    applicationNumber: getApplicationNumberValidationSchema({ t }),
    isAgreeWithConditions: Yup.boolean()
      .required(t(ErrorMessages.MustBeAccepted))
      .oneOf([true], t(ErrorMessages.MustBeAccepted)),
  });

export const paymentCardServiceInstructionApplicationValidationSchema = (
  { t }: ValidationSchemaProps
) =>
  Yup.object().shape({
    applicationNumber: getApplicationNumberValidationSchema({ t }),
    purpose: getPurposeValidationSchema({ t }),
    action: getRequiredStringValidationSchema({ t }),
    creditCardNumber: getCardNumberValidationSchema({ t }),
    phoneNumber: getPhoneNumberValidationSchema({ t }),
    isAgreeWithConditions: Yup.boolean()
      .required(t(ErrorMessages.MustBeAccepted))
      .oneOf([true], t(ErrorMessages.MustBeAccepted)),
    doInshurance: Yup.boolean().when(
      "action",
      {
        is: InstructionActions.CardInsurance,
        then: (schema) =>
          schema
            .required(t(ErrorMessages.RequiredField))
            .oneOf([true, false]),
        otherwise: (schema) => schema.nullable(),
      }
    ),
    passwordReceivingPhoneNumber: Yup.string().when(
      "action",
      {
        is: InstructionActions.PasswordChange,
        then: () => getPhoneNumberValidationSchema({ t }),
        otherwise: (schema) => schema.nullable(),
      }
    ),
    cardReissuanceDetails: Yup.object().when(
      "action",
      {
        is: InstructionActions.CardReissuance,
        then: () =>
          Yup.object().shape({
            cardReissuanceReason: Yup.string().required(
              t(ErrorMessages.RequiredField)
            ),
            reissuedCardReceivingType: Yup.string().required(
              t(ErrorMessages.RequiredField)
            ),
            cardChangeType: Yup.string().when(
              "cardReissuanceReason",
              {
                is: CardReissuanceReason.CardTypeChange,
                then: (schema) =>
                  schema.required(t(ErrorMessages.RequiredField)),
                otherwise: (schema) => schema.nullable(),
              }
            ),
            cardType: Yup.string().when(
              "cardReissuanceReason",
              {
                is: CardReissuanceReason.CardTypeChange,
                then: (schema) =>
                  schema.required(t(ErrorMessages.RequiredField)),
                otherwise: (schema) => schema.nullable(),
              }
            ),
            reissuedCardReceivingAddress: Yup.string().when(
              "reissuedCardReceivingType",
              {
                is: ReissuedCardReceivingType.Delivery,
                then: (schema) =>
                  schema.required(t(ErrorMessages.RequiredField)),
                otherwise: (schema) => schema.nullable(),
              }
            ),
            bankBranchCode: Yup.string().when(
              "reissuedCardReceivingType",
              {
                is: ReissuedCardReceivingType.BankBranch,
                then: (schema) =>
                  schema.required(t(ErrorMessages.RequiredField)),
                otherwise: (schema) => schema.nullable(),
              }
            ),
          }),
        otherwise: () =>
          Yup.object().shape({
            cardReissuanceReason: Yup.string().nullable(),
            reissuedCardReceivingType: Yup.string().nullable(),
            cardChangeType: Yup.string().nullable(),
            cardType: Yup.string().nullable(),
            reissuedCardReceivingAddress: Yup.string().nullable(),
            bankBranchCode: Yup.string().nullable(),
          }),
      }
    ),
  });

export const userOpeningApplicationValidationSchema = (
  { t }: ValidationSchemaProps) =>
  Yup.object().shape({
    username: getRequiredStringValidationSchema({ t })
      .matches(LATIN_NUMERIC_REGEX, t(ErrorMessages.InvalidUsername))
      .min(3, t(ErrorMessages.MinInput, { count: 3 }))
      .max(50, t(ErrorMessages.MaxInput, { count: 50 })),
    name: getRequiredStringValidationSchema({ t })
      .min(3, t(ErrorMessages.MaxInput, { count: 3 }))
      .max(140, t(ErrorMessages.MinInput, { count: 140 })),
    surname: getRequiredStringValidationSchema({ t })
      .min(3, t(ErrorMessages.MinInput, { count: 3 }))
      .max(140, t(ErrorMessages.MaxInput, { count: 140 })),
    emailAddress: getRequiredStringValidationSchema({ t })
      .email(t(ErrorMessages.InvalidEmail)),
    phoneNumber: getPhoneNumberValidationSchema({ t }),
    passportData: getRequiredStringValidationSchema({ t })
      .matches(LATIN_NUMERIC_REGEX, t(ErrorMessages.InvalidDocumentNumber))
      .min(5, t(ErrorMessages.MinInput, { count: 5 }))
      .max(100, t(ErrorMessages.MaxInput, { count: 100 })),
    purpose: getPurposeValidationSchema({ t }),
    documentFile: Yup.mixed<File[]>()
      .required(t(ErrorMessages.RequiredField))
      .test("fileSize", t(ErrorMessages.FileSize), (files) => {
        const file = files[0];
        return file ? file.size <= MAX_FILE_SIZE : false;
      })
      .test("fileType", t(ErrorMessages.FileType), (files) => {
        const file = files[0];
        return file ? SUPPORTED_DOCUMENT_FILE_TYPES.includes(file.type) : false;
      }),
    roles: Yup.array()
      .of(Yup.string().required(t(ErrorMessages.RequiredField)))
      .min(1, t(ErrorMessages.RequiredField))
      .required(t(ErrorMessages.RequiredField)),
    applicationNumber: getApplicationNumberValidationSchema({ t }),
  });

export const loanTrancheDisbursementApplicationValidationSchema = (
  { t }: ValidationSchemaProps) =>
  Yup.object().shape({
    loanContractNumber: getRequiredStringValidationSchema({ t }),
    trancheAmount: getAmountValidationSchema({ t }),
    purpose: getPurposeValidationSchema({ t }),
    applicationNumber: getApplicationNumberValidationSchema({ t }),
  });

export const depositTerminationApplicationValidationSchema = (
  { t }: ValidationSchemaProps) =>
  Yup.object().shape({
    depositNumber: getRequiredStringValidationSchema({ t }),
    purpose: getPurposeValidationSchema({ t }),
    applicationNumber: getApplicationNumberValidationSchema({ t }),
    isAgreeWithConditions: Yup.boolean()
      .required(t(ErrorMessages.MustBeAccepted))
      .oneOf([true], t(ErrorMessages.MustBeAccepted)),
  });
